import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, History, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Tiny sticky indicator (bottom-right) that shows whenever any backend
 * banking-backfill orchestrator is running. Visible on every page so users
 * know their backfill is still progressing even after navigating away.
 */
export default function GlobalBackfillIndicator() {
  const [txnState, setTxnState] = useState(null);
  const [signupState, setSignupState] = useState(null);
  const [bulkState, setBulkState] = useState(null);
  const interval = useRef(null);
  const anyRunning = txnState?.running || signupState?.running || bulkState?.running;

  useEffect(() => {
    const poll = async () => {
      try {
        const [a, b, c] = await Promise.all([
          fetch(`${API}/api/banking/backfill/status`).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/banking/signup-cache/status`).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/banking/signup-cache/bulk-status`).then(r => r.ok ? r.json() : null),
        ]);
        setTxnState(a); setSignupState(b); setBulkState(c);
      } catch { /* noop */ }
    };
    poll();
    // Fast poll (3s) when a job is running, slow poll (45s) when idle.
    // Pause entirely while the tab is hidden.
    const delay = anyRunning ? 3000 : 45000;
    const start = () => {
      if (interval.current) return;
      interval.current = setInterval(poll, delay);
    };
    const stop = () => {
      if (interval.current) { clearInterval(interval.current); interval.current = null; }
    };
    if (document.visibilityState === 'visible') start();
    const onVis = () => {
      if (document.visibilityState === 'visible') { poll(); start(); } else stop();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [anyRunning]);

  const items = [];
  if (txnState?.running) {
    const pct = txnState.total_windows ? Math.round((txnState.windows_done / txnState.total_windows) * 100) : 0;
    items.push({ label: 'Txn backfill', detail: `${txnState.windows_done}/${txnState.total_windows} · +${txnState.records_added}`, pct });
  }
  if (signupState?.running) {
    const pct = signupState.total ? Math.round((signupState.done / signupState.total) * 100) : 0;
    items.push({ label: 'Signup fetch', detail: `${signupState.done}/${signupState.total}`, pct });
  }
  if (bulkState?.running) {
    const pct = bulkState.total_days ? Math.round((bulkState.days_done / bulkState.total_days) * 100) : 0;
    items.push({ label: 'Signup bulk', detail: `${bulkState.days_done}/${bulkState.total_days} · +${bulkState.rows_upserted}`, pct });
  }
  if (items.length === 0) return null;

  return (
    <Link
      to="/banking"
      data-testid="global-backfill-indicator"
      className="fixed bottom-4 right-4 z-50 bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 hover:border-violet-300 transition-colors"
      style={{ minWidth: 220 }}
    >
      <div className="flex items-center gap-2 mb-1">
        <History className="w-3.5 h-3.5 text-violet-600 animate-pulse" />
        <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700">Background</span>
        <ChevronRight className="w-3 h-3 text-slate-300 ml-auto" />
      </div>
      {items.map((it, i) => (
        <div key={i} className="mb-1 last:mb-0">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-700 font-medium flex items-center gap-1">
              <RefreshCw className="w-3 h-3 animate-spin text-slate-500" />
              {it.label}
            </span>
            <span className="text-[10px] text-slate-500 font-mono">{it.detail}</span>
          </div>
          <div className="h-1 bg-slate-100 rounded-full mt-0.5 overflow-hidden">
            <div className="h-full bg-violet-500 transition-all" style={{ width: `${it.pct}%` }} />
          </div>
        </div>
      ))}
    </Link>
  );
}
